import { useSyncExternalStore } from 'react';
import type { DisplayFormatConfig, TabDisplayFormat } from '@mitang/shared';
import { listenDisplayFormat } from '../lib/repo';

/**
 * Single shared app_config/displayFormat listener (same pattern as
 * useAppConfig): per-tab labels, table gaps and share-message layout, live —
 * an admin edit reaches an open product screen without a reload.
 */
let config: DisplayFormatConfig | null = null;
let started = false;
let refCount = 0;
let unsub: (() => void) | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

function ensureStarted(): void {
  if (started) return;
  started = true;
  unsub = listenDisplayFormat(
    (c) => {
      config = c;
      emit();
    },
    () => {
      // Keep last-known config on error/offline.
    },
  );
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  refCount += 1;
  ensureStarted();
  return () => {
    listeners.delete(cb);
    refCount -= 1;
    if (refCount === 0 && unsub) {
      unsub();
      unsub = null;
      started = false;
    }
  };
}

function getSnapshot(): DisplayFormatConfig | null {
  return config;
}

export function useDisplayFormat(): DisplayFormatConfig | null {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** The one tab's config (or null), by product category / sheet tab name. */
export function useTabDisplayFormat(category: string | null | undefined): TabDisplayFormat | null {
  const all = useDisplayFormat();
  return (category ? all?.tabs?.[category] : null) ?? null;
}
